import config from "./config";
import toast from "react-hot-toast";
import {EndpointScope, TokenRequest, mqttEndpointScopes} from "./Token";
import {Endpoint} from "./Endpoints";

export enum AuthMode {
    CLIENT_CREDENTIALS = "client_credentials",
    OIDC = "oidc"
}

interface OIDCDiscoveryDocument {
    authorization_endpoint: string;
    token_endpoint: string;
    end_session_endpoint?: string;
    issuer: string;
    jwks_uri: string;
    response_types_supported: string[];
    code_challenge_methods_supported?: string[];
}

class AuthService {
    private token: string = "";
    private authMode: AuthMode = AuthMode.CLIENT_CREDENTIALS;
    private discoveryCache: OIDCDiscoveryDocument | null = null;
    private discoveryPromise: Promise<OIDCDiscoveryDocument> | null = null;

    public getAuthMode(): AuthMode {
        const storedMode = sessionStorage.getItem("auth_mode") as AuthMode;
        if (storedMode && Object.values(AuthMode).includes(storedMode)) {
            this.authMode = storedMode;
        }
        return this.authMode;
    }

    public setAuthMode(mode: AuthMode): void {
        this.authMode = mode;
        sessionStorage.setItem("auth_mode", mode);
    }

    private isOIDCConfigured(): boolean {
        return !!(config.OAuthProviderUrl && config.OidcClientId);
    }

    public getApiBaseUrl(): string {
        return this.getAuthMode() === AuthMode.OIDC ? config.AuthProxyUrl : config.ApiUrl;
    }

    public async apiRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
        const baseUrl = this.getApiBaseUrl();
        const url = baseUrl + endpoint;
        
        // Add authentication header
        const headers = new Headers(options.headers);
        headers.set('Authorization', this.authHeader());
        
        const requestOptions: RequestInit = {
            ...options,
            headers
        };

        return fetch(url, requestOptions);
    }

    public authHeader(): string {
        if (this.token == "") {
            this.token = sessionStorage.getItem("token") || "";
        }
        if (this.isTokenExpired(this.token)) {
            this.login(sessionStorage.getItem("client_id") || "", sessionStorage.getItem("client_secret") || "", () => {return "Bearer " + this.token;});
        }
        return "Bearer " + this.token;        
    }

    private isTokenExpired(token: string): boolean {
        if (!token || token === "") return true;
        const payloadBase64 = token.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        const exp = payload.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        return exp < currentTime;
    }

    public isLoggedIn(): boolean {
        return !this.isTokenExpired(sessionStorage.getItem("token") || "");
    }

    public async getMqttOptions(endpointId: string): Promise<Paho.MQTT.ConnectionOptions> {
        if (this.getAuthMode() === AuthMode.OIDC) {
            // Use auth-proxy endpoint for OIDC mode
            const res = await fetch(config.AuthProxyUrl + "/token/endpoint/" + endpointId, {
                "method": "POST",
                headers: [
                    ["Authorization", this.authHeader()],
                    ["Accept", "application/json"]
                ]
            });

            if (res.ok && res.body) {
                const token = await res.text();
                return {
                    userName: token,
                    password: ""
                };
            } else {
                toast.error("Endpoint token request failed");
                return {
                    userName: "",
                    password: ""
                };
            }
        } else {
            // Use direct WEMS API for client credentials mode (current behavior)
            const tokenLoginRequest: TokenRequest = {
                client_id: sessionStorage.getItem("client_id") || "",
                client_secret: sessionStorage.getItem("client_secret") || "",
                endpoints: {
                    [endpointId]: mqttEndpointScopes
                },
                platform_scopes: [],
                application_components: {},
                super_token: false
            }
            const res = await fetch(config.ApiUrl + "/v1/token", {
                "method": "POST",
                headers: [
                    ["Content-Type", "application/json"],
                    ["Accept", "application/json"]
                ],
                body: JSON.stringify(tokenLoginRequest)
            });

            if (res.ok && res.body) {
                const token = await res.text();
                return {
                    userName: token,
                    password: ""
                };
            } else {
                toast.error("Endpoint token request failed");
                return {
                    userName: "",
                    password: ""
                };
            }
        }
    }

    public login(username: string, password: string, callback: () => void): void {
        if (this.getAuthMode() === AuthMode.OIDC) {
            if (!this.isOIDCConfigured()) {
                throw new Error("OIDC authentication requested but not configured");
            }
            // OIDC login will be handled by redirect flow
            this.initiateOidcLogin();
            return;
        }

        // Client credentials login (existing behavior)
        const tokenLoginRequest: TokenRequest = {
            client_id: username,
            client_secret: password,
            endpoints: {},
            platform_scopes: [],
            application_components: {},
            super_token: true
        }
        fetch(config.ApiUrl + "/v1/token", {
            "method": "POST",
            headers: [
                ["Content-Type", "application/json"],
                ["Accept", "application/json"]
            ],
            body: JSON.stringify(tokenLoginRequest)
        }).then((res) => {
            if (res.ok) {                
                if (res.body) {
                    res.text().then((token) => {
                        sessionStorage.setItem("token", token);
                        sessionStorage.setItem("client_id", username);
                        sessionStorage.setItem("client_secret", password);
                        this.token = token;
                    }).then(() => {                     
                        toast.success('Welcome, ' + username + '!');
                        callback();  
                    });
                } else {
                    toast.error("Failed to retrieve token");
                }
            } else {
                toast.error("Login failed");
                callback();
            }
        })
    }

    private async initiateOidcLogin(): Promise<void> {
        if (!this.isOIDCConfigured()) {
            throw new Error("OIDC is not configured - missing OAuthProviderUrl or OidcClientId");
        }

        try {
            // Get OIDC discovery endpoints
            const discovery = await this.getOIDCDiscovery();
            
            // Generate state parameter for security
            const state = this.generateRandomString(32);
            const codeVerifier = this.generateCodeVerifier();
            const codeChallenge = await this.generateCodeChallenge(codeVerifier);

            // Store state and code verifier for validation
            sessionStorage.setItem("auth_state", state);
            sessionStorage.setItem("code_verifier", codeVerifier);

            // Build authorization URL for PKCE flow using discovered endpoint
            const authUrl = new URL(discovery.authorization_endpoint);
            authUrl.searchParams.append("response_type", "code");  // Authorization code flow
            authUrl.searchParams.append("state", state);
            authUrl.searchParams.append("code_challenge", codeChallenge);
            authUrl.searchParams.append("code_challenge_method", "S256");
            authUrl.searchParams.append("redirect_uri", window.location.origin + "/auth/callback");
            
            // Add required OIDC parameters
            if (config.OidcClientId) {
                authUrl.searchParams.append("client_id", config.OidcClientId);
            }
            if (config.OidcScopes) {
                authUrl.searchParams.append("scope", config.OidcScopes);
            }

            // Redirect to OIDC provider
            window.location.href = authUrl.toString();
        } catch (error) {
            throw new Error(`Failed to initiate OIDC login: ${error}`);
        }
    }

    public async handleOidcCallback(): Promise<boolean> {
        // For PKCE flow, authorization code comes in query parameters
        const urlParams = new URLSearchParams(window.location.search);
        
        const authorizationCode = urlParams.get("code");
        const state = urlParams.get("state");
        const error = urlParams.get("error");
        
        const storedState = sessionStorage.getItem("auth_state");
        const storedCodeVerifier = sessionStorage.getItem("code_verifier");
        
        try {
            // Check for errors
            if (error) {
                return false;
            }

            // Validate state parameter
            if (!state || state !== storedState) {
                return false;
            }

            // Check if we have the required authorization code
            if (!authorizationCode) {
                return false;
            }

            // Check if we have the stored code verifier
            if (!storedCodeVerifier) {
                return false;
            }

            // Exchange authorization code for tokens
            const tokenResponse = await this.exchangeCodeForTokens(authorizationCode, storedCodeVerifier);
            if (!tokenResponse) {
                return false;
            }

            // Store tokens
            sessionStorage.setItem("token", tokenResponse.access_token);
            if (tokenResponse.id_token) {
                sessionStorage.setItem("id_token", tokenResponse.id_token);
            }
            this.token = tokenResponse.access_token;

            // Clean up stored parameters
            sessionStorage.removeItem("auth_state");
            sessionStorage.removeItem("code_verifier");

            return true;
        } catch (error) {
            return false;
        }
    }

    private async exchangeCodeForTokens(authorizationCode: string, codeVerifier: string): Promise<any> {
        try {
            // Get discovered token endpoint
            const discovery = await this.getOIDCDiscovery();
            
            const requestBody = new URLSearchParams({
                grant_type: "authorization_code",
                client_id: config.OidcClientId || "",
                code: authorizationCode,
                redirect_uri: window.location.origin + "/auth/callback",
                code_verifier: codeVerifier
            });

            const response = await fetch(discovery.token_endpoint, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json"
                },
                body: requestBody.toString()
            });

            if (!response.ok) {
                return null;
            }

            return await response.json();
        } catch (error) {
            return null;
        }
    }


    private async getOIDCDiscovery(): Promise<OIDCDiscoveryDocument> {
        if (!this.isOIDCConfigured()) {
            throw new Error("OIDC is not configured - cannot fetch discovery document");
        }

        // Return cached discovery if available
        if (this.discoveryCache) {
            return this.discoveryCache;
        }

        // Avoid multiple concurrent discovery requests
        if (this.discoveryPromise) {
            return this.discoveryPromise;
        }

        // Start the discovery request
        this.discoveryPromise = this.fetchOIDCDiscovery();
        this.discoveryCache = await this.discoveryPromise;
        this.discoveryPromise = null; // Clear promise after completion
        
        return this.discoveryCache;
    }

    private async fetchOIDCDiscovery(): Promise<OIDCDiscoveryDocument> {
        const discoveryUrl = config.OAuthProviderUrl + "/.well-known/openid-configuration";
        
        try {
            const response = await fetch(discoveryUrl);
            if (!response.ok) {
                throw new Error(`Discovery failed: ${response.status}`);
            }
            
            const discovery: OIDCDiscoveryDocument = await response.json();
            
            // Validate required endpoints
            if (!discovery.authorization_endpoint || !discovery.token_endpoint) {
                throw new Error("Invalid discovery document - missing required endpoints");
            }
            
            return discovery;
        } catch (error) {
            throw new Error(`OIDC Discovery failed for ${config.OAuthProviderUrl}: ${error}`);
        }
    }

    private generateRandomString(length: number): string {
        const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i = 0; i < length; i++) {
            result += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return result;
    }

    private generateCodeVerifier(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return this.base64URLEncode(array);
    }

    private async generateCodeChallenge(verifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const digest = await crypto.subtle.digest('SHA-256', data);
        return this.base64URLEncode(new Uint8Array(digest));
    }

    private base64URLEncode(array: Uint8Array): string {
        return btoa(String.fromCharCode(...array))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    public logout(): void {
        const currentAuthMode = this.getAuthMode();
        
        // Clear session storage
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("client_id");
        sessionStorage.removeItem("client_secret");
        sessionStorage.removeItem("id_token");
        sessionStorage.removeItem("auth_mode");
        sessionStorage.removeItem("auth_state");
        sessionStorage.removeItem("auth_nonce");
        sessionStorage.removeItem("code_verifier");
        this.token = "";
        this.authMode = AuthMode.CLIENT_CREDENTIALS;

        // If using OIDC, redirect to OIDC logout endpoint
        if (currentAuthMode === AuthMode.OIDC) {
            this.performOidcLogout();
        }
    }

    private async performOidcLogout(): Promise<void> {
        if (!this.isOIDCConfigured()) {
            // Silently skip OIDC logout if not configured
            return;
        }

        try {
            // Get discovered logout endpoint
            const discovery = await this.getOIDCDiscovery();
            
            if (!discovery.end_session_endpoint) {
                throw new Error("No logout endpoint found in OIDC discovery document");
            }
            
            const logoutUrl = new URL(discovery.end_session_endpoint);
            logoutUrl.searchParams.append("client_id", config.OidcClientId || "");
            logoutUrl.searchParams.append("post_logout_redirect_uri", window.location.origin);
            
            window.location.href = logoutUrl.toString();
        } catch (error) {
            // Silently handle OIDC logout errors - user is still logged out locally
            console.warn("OIDC logout failed:", error);
        }
    }
}
const authService = new AuthService();
export default authService;
